/**
 * Post Generation Multi-AI Page
 * Feature 014: Unified Multi-AI Post Generation Flow
 * T047-T049: Main page for research + multi-model post generation
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAction, useQuery } from 'convex/react';
import { Id } from '../../convex/_generated/dataModel';
import { api } from '../../convex/_generated/api';
import { usePostGenerationMultiAI } from '../hooks/usePostGeneration';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { ResearchSkeletonMemo } from '../components/post-generation/ResearchSkeleton';
import { PostGenerationSkeletonMemo } from '../components/post-generation/PostGenerationSkeleton';
import { MultiModelPostDisplayMemo } from '../components/post-generation/MultiModelPostDisplay';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../components/ui/dialog';

const PostGenerationMultiAIPage = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get postId from URL params if provided, otherwise we'll create one
  const postIdFromUrl = searchParams.get('postId');

  // Get business profile for post creation
  const { profile } = useBusinessProfile();

  // Create post action (calls backend which has all the required fields)
  const createPostForTopicAction = useAction(api.ai.postGeneration.createPostForTopic);

  // T048: Integrate hooks - initially undefined until post is created/loaded
  const { state, handleStartGeneration, handleSelectBestVariant } = usePostGenerationMultiAI(
    postIdFromUrl as Id<'posts'> | undefined,
    topicId as Id<'topics'>
  );

  // Fetch topic data to display topic name
  const topic = useQuery(
    api.topics.getTopicById,
    topicId ? { topicId: topicId as Id<'topics'> } : 'skip'
  );

  // Dialog state for showing research results
  const [isResearchDialogOpen, setIsResearchDialogOpen] = useState(false);

  // T049: Auto-trigger post creation and research on page load
  useEffect(() => {
    if (!topicId || !profile) return;

    const initializeGeneration = async () => {
      try {
        // If we don't have a postId yet, create one
        if (!postIdFromUrl) {
          const newPostId = await createPostForTopicAction({
            topicId: topicId as Id<'topics'>,
            businessId: profile._id,
          });

          // Update URL with new postId
          navigate(`/post-generation/${topicId}?postId=${newPostId}`, { replace: true });
          return;
        }

        // CRITICAL: Wait for selectedModels to be populated before starting generation
        // If we have a postId, we're in idle phase, AND selectedModels are loaded
        if (postIdFromUrl && state.phase === 'idle' && state.selectedModels.length > 0) {
          void handleStartGeneration();
        }
      } catch {
        // Error will be shown in UI via state
      }
    };

    void initializeGeneration();
  }, [
    topicId,
    profile,
    postIdFromUrl,
    state.phase,
    state.selectedModels.length,
    handleStartGeneration,
    createPostForTopicAction,
    navigate,
  ]);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Loading state while waiting for profile, topic, or creating post
  const isInitializing = !profile || !topic || (!postIdFromUrl && state.phase === 'idle');

  if (isInitializing) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
        <div className="mt-8 h-64 animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  // Topic not found
  if (!isInitializing && topic === null) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="font-semibold text-red-900">Topic Not Found</h3>
          <p className="mt-2 text-sm text-red-700">The requested topic could not be found.</p>
          <Button onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleBack}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{topic ? topic.name : 'Loading...'}</h1>
        <p className="mt-2 text-gray-600">
          Comparing posts from {state.selectedModels.length} AI model
          {state.selectedModels.length > 1 ? 's' : ''}:{' '}
          {state.selectedModels
            .map(m => (m === 'claude' ? 'Claude' : m === 'gemini' ? 'Gemini' : 'GPT'))
            .join(', ')}
        </p>
      </div>

      {/* Research Phase */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Research</h2>
        {state.phase === 'idle' && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
            <p className="text-gray-600">Starting research...</p>
          </div>
        )}
        {state.researchLoading && <ResearchSkeletonMemo />}
        {state.researchError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <h3 className="font-semibold text-red-900">Research Failed</h3>
            <p className="mt-2 text-sm text-red-700">{state.researchError}</p>
            <Button onClick={handleStartGeneration} className="mt-4">
              Retry
            </Button>
          </div>
        )}
        {state.researchResults && (
          <Button
            variant="outline"
            onClick={() => setIsResearchDialogOpen(true)}
            className="w-full sm:w-auto"
          >
            Show research
          </Button>
        )}
      </div>

      {/* Post Generation Phase */}
      {(state.phase === 'generation' || state.phase === 'completed') && (
        <div>
          <h2 className="mb-4 text-xl font-semibold text-gray-900">Choose best variant</h2>

          {/* Show skeleton if all models are still idle */}
          {state.selectedModels.every(m => state.generationStatus[m] === 'idle') && (
            <PostGenerationSkeletonMemo modelCount={state.selectedModels.length} />
          )}

          {/* Show multi-model display once at least one model starts */}
          {state.selectedModels.some(m => state.generationStatus[m] !== 'idle') && (
            <MultiModelPostDisplayMemo
              selectedModels={state.selectedModels}
              generatedPosts={state.generatedPosts}
              generationStatus={state.generationStatus}
              selectedVariantModel={state.selectedVariantModel}
              isSelecting={state.isSelecting}
              onSelectBestVariant={handleSelectBestVariant}
            />
          )}
        </div>
      )}

      {/* Research Dialog */}
      {state.researchResults && (
        <Dialog open={isResearchDialogOpen} onOpenChange={setIsResearchDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>Research Results</span>
                {state.researchCached && (
                  <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                    Cached
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="prose prose-sm max-w-none mt-4">
              <p className="whitespace-pre-wrap text-gray-700">{state.researchResults}</p>
            </div>

            <DialogClose className="absolute right-4 top-4" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export { PostGenerationMultiAIPage };
